/**
 * Finder filter model — the pure, testable predicate + summary helpers the W0
 * screen shares across the filter bar, letter chips and result count. Data
 * fetching itself lives in the nested WellRow / EventChip components (each owns
 * its own RTK Query hook, so the finder stays fully reactive as events + plan
 * status stream in). This module has no React and no I/O.
 */
import type { WellView } from "@api/mappers";
import type { EventOut } from "@api/types";
import { enrichEventCode, type EventLetter } from "@lib/eventEnrichment";

export interface FinderFilters {
  basin: string;
  field: string;
  pad: string;
  query: string;
  /** Selected event-type letters (empty = any). */
  letters: Set<EventLetter>;
  /** Conventional filter: "conv" | "unconv" | null (any). */
  conv: "conv" | "unconv" | null;
}

export const EMPTY_FILTERS: FinderFilters = {
  basin: "",
  field: "",
  pad: "",
  query: "",
  letters: new Set(),
  conv: null,
};

/** Does an event pass the letter + conventional filter? */
export function eventPasses(event: EventOut, filters: FinderFilters): boolean {
  const en = enrichEventCode(event.event_code);
  const conv = en.isConventional ?? event.is_conventional;
  const letterOk = filters.letters.size === 0 || (en.letter != null && filters.letters.has(en.letter));
  const convOk =
    !filters.conv ||
    (filters.conv === "conv" && conv === true) ||
    (filters.conv === "unconv" && conv === false);
  return letterOk && convOk;
}

/** Does a well pass the location/text filters (event filter checked separately)? */
export function wellPasses(well: WellView, filters: FinderFilters): boolean {
  if (filters.basin && well.basin !== filters.basin) return false;
  if (filters.field && well.field !== filters.field) return false;
  if (filters.pad && well.pad !== filters.pad) return false;
  const q = filters.query.trim().toLowerCase();
  if (q && !(well.commonName.toLowerCase().includes(q) || well.uwi.toLowerCase().includes(q)))
    return false;
  return true;
}

/** True if any filter is active (drives the "clear" affordance). */
export function hasActiveFilters(f: FinderFilters): boolean {
  return Boolean(f.basin || f.field || f.pad || f.query.trim() || f.letters.size || f.conv);
}

/** Distinct sorted values of a WellView key across the register (for selects). */
export function distinctValues(wells: WellView[], key: "basin" | "field" | "pad"): string[] {
  return Array.from(new Set(wells.map((w) => w[key]).filter((v) => v && v !== "—"))).sort();
}
